import {graphql, useFragment} from 'react-relay'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {NestedListView} from '@github-ui/nested-list-view'
import {NestedListViewHeader} from '@github-ui/nested-list-view/NestedListViewHeader'
import {NestedListViewHeaderTitle} from '@github-ui/nested-list-view/NestedListViewHeaderTitle'
import {NestedListViewCompletionPill} from '@github-ui/nested-list-view/NestedListViewCompletionPill'

import {SubIssuesListItem} from './SubIssuesListItem'
import type {SubIssuesListView$key} from './__generated__/SubIssuesListView.graphql'

const SubIssuesListViewGraphQLFragment = graphql`
  fragment SubIssuesListView on Issue {
    id
    subIssues(first: 50) {
      nodes {
        id
        ...SubIssuesListItem
      }
    }
    subIssuesSummary {
      total
      completed
      percentCompleted
    }
  }
`

export function SubIssuesListView({
  onSubIssueClick,
  issueKey,
}: {
  /** Callback called when a sub-issue is clicked */
  onSubIssueClick?: (itemIdentifier: ItemIdentifier, url: string) => void
  issueKey: SubIssuesListView$key
}) {
  const data = useFragment(SubIssuesListViewGraphQLFragment, issueKey)

  if (!data.subIssues) return null
  if (!data.subIssuesSummary) return null

  const {total, completed, percentCompleted} = data.subIssuesSummary

  return (
    <NestedListView
      title="Sub-issues"
      header={
        <NestedListViewHeader
          title={<NestedListViewHeaderTitle title="Sub issues" />}
          completionPill={
            <NestedListViewCompletionPill
              progress={{
                total,
                completed,
                percentCompleted,
              }}
            />
          }
          sx={{background: 'none', height: 40, borderBottom: 0}}
        />
      }
      isCollapsible={true}
      listItems={
        data.subIssues?.nodes?.map(issueNode => {
          if (!issueNode || !issueNode?.id) return {id: '', title: '', node: null}
          return {
            id: issueNode.id,
            title: issueNode.id,
            node: issueNode,
          }
        }) || []
      }
      // TODO: update the ondrop callback when we have a way to update the priority of an item
      onDrop={() => {}}
    >
      {data.subIssues.nodes?.map(issueNode => {
        if (!issueNode) return null
        if (!data.id) return null
        return (
          <SubIssuesListItem
            key={issueNode.id}
            issueKey={issueNode}
            parentIssueId={data.id}
            onSubIssueClick={onSubIssueClick}
            dnd={true}
          />
        )
      })}
    </NestedListView>
  )
}

try{ SubIssuesListView.displayName ||= 'SubIssuesListView' } catch {}